import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, IconButton, Badge, Tooltip, styled } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import HubIcon from '@mui/icons-material/Hub';
import { ClientCardDialog } from './ClientCardDialog';
import { GroupInfoDialog } from './GroupInfoDialog';
import { groupSubtitle } from './groupPermissions';
import { useOrg } from '../../context/OrgContext';
import { GroupDetails } from '../../types';


// Твой StyledBadge
const StyledBadge = styled(Badge, {
    shouldForwardProp: (prop) => prop !== 'isOnline',
})<{ isOnline?: boolean }>(({ theme, isOnline }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: isOnline ? '#44b700' : '#bdbdbd',
        color: isOnline ? '#44b700' : '#bdbdbd',
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        '&::after': isOnline ? {
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            borderRadius: '50%', animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor', content: '""',
        } : {},
    },
    '@keyframes ripple': { '0%': { transform: 'scale(.8)', opacity: 1 }, '100%': { transform: 'scale(2.4)', opacity: 0 } },
}));

const formatLastSeen = (date?: string) => {
    if (!date) return 'давно';
    const lastSeen = new Date(date);
    const now = new Date();
    const diffInSec = Math.floor((now.getTime() - lastSeen.getTime()) / 1000);
    if (diffInSec < 60) return 'только что';
    if (diffInSec < 3600) return `${Math.floor(diffInSec / 60)} мин. назад`;
    if (diffInSec < 86400) return `${Math.floor(diffInSec / 3600)} ч. назад`;
    return lastSeen.toLocaleDateString();
};

// Твоя функция иконок каналов
const getChannelIcon = (type?: string) => {
    switch (type) {
        case 'whatsapp': return <WhatsAppIcon sx={{ ml: 1, fontSize: 18, color: '#25D366' }} />;
        case 'telegram': return <TelegramIcon sx={{ ml: 1, fontSize: 18, color: '#0088cc' }} />;
        default: return <HubIcon sx={{ ml: 1, fontSize: 18, color: '#9c27b0' }} />;
    }
};

interface ChatHeaderProps {
    conversation: any;
    chatPartner: any;
    isContactOnline: boolean;
    isMobile?: boolean;
    onBack: () => void;
    /** id текущего пользователя: по нему карточка группы подсвечивает «вы». */
    currentUserId?: string | null;
    /** Группа удалена или пользователь вышел из неё — чат нужно закрыть. */
    onGroupRemoved?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
                                                          conversation,
                                                          chatPartner,
                                                          isContactOnline,
                                                          isMobile,
                                                          onBack,
                                                          currentUserId = null,
                                                          onGroupRemoved
                                                      }) => {
    const [cardOpen, setCardOpen] = useState(false);
    const [groupInfoOpen, setGroupInfoOpen] = useState(false);
    // Свежие название и число участников после правок в карточке группы:
    // беседа не перезапрашивается, поэтому держим их локально.
    const [groupMeta, setGroupMeta] = useState<{ title: string; memberCount: number } | null>(null);
    // Карточка клиента ищет чаты в выбранной организации ('' — все / личное).
    const { orgId } = useOrg();

    const conversationId: string | null = conversation?.id ?? null;

    // Другая беседа — чужие название и счётчик в шапке не показываем.
    useEffect(() => {
        setGroupMeta(null);
        setGroupInfoOpen(false);
    }, [conversationId]);

    if (!conversation) return null;
    const isGroup = conversation.payload?.isGroup || conversation.isGroup; // Флаг из API
    const displayName = isGroup
        ? (groupMeta?.title || conversation.title || 'Групповой чат')
        : (conversation.contact?.mainName || 'Беседа');

    const showOnline = !isGroup && (chatPartner?.isOnline || isContactOnline);

    // Бэкенд сам достаёт номер из беседы или метаданных контакта и отдаёт
    // его в contact.phone; остальные варианты оставлены для старых ответов,
    // а по id беседы карточка откроется, даже если номера нет вовсе.
    const clientPhone: string | null =
        conversation.contact?.phone
        ?? conversation.contact?.phoneNumber
        ?? conversation.phone
        ?? conversation.phoneNumber
        ?? chatPartner?.phone
        ?? chatPartner?.phoneNumber
        ?? null;
    // Количество участников может прийти с беседой или уже из карточки группы.
    const membersCount: number | null =
        groupMeta?.memberCount
        ?? conversation.membersCount
        ?? conversation.payload?.membersCount
        ?? null;

    const handleGroupUpdated = (group: GroupDetails) => {
        setGroupMeta({ title: group.title, memberCount: group.memberCount });
    };

    return (
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', zIndex: 1100, flexShrink: 0,  }}>
            {isMobile && (
                <IconButton onClick={onBack} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
            )}

            <StyledBadge
                isOnline={chatPartner?.isOnline || isContactOnline}
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant={isGroup ? "standard" : "dot"}
            >
                <Avatar sx={{
                    bgcolor: isGroup ? 'secondary.main' : (conversation.type === 'orion' ? '#9c27b0' : 'primary.main')
                }}>
                    {isGroup ? <GroupIcon /> : (displayName[0] || 'U')}
                </Avatar>
            </StyledBadge>

            <Box sx={{ ml: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography noWrap variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {displayName}
                    </Typography>
                    {/* Иконка канала (ТГ/ВК) остается актуальной и для групп */}
                    {getChannelIcon(conversation.type)}
                </Box>
                <Typography
                    variant="caption"
                    sx={{ color: showOnline ? "#44b700" : "text.secondary" }}
                >
                    {isGroup ? (
                        // «ГРУППА • 5 участников»: число берём из беседы или карточки.
                        groupSubtitle(membersCount)
                    ) : (
                        showOnline
                            ? 'в сети'
                            : `был(а) ${formatLastSeen(chatPartner?.lastSeen)}`
                    )}
                </Typography>
            </Box>

            <Box sx={{ ml: 'auto', pl: 1 }}>
                {isGroup ? (
                    // У группы нет телефона клиента — вместо карточки клиента
                    // открываем состав и управление группой.
                    <Tooltip title="О группе">
                        <IconButton
                            onClick={() => setGroupInfoOpen(true)}
                            aria-label="Информация о группе"
                        >
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Карточка клиента">
                        <IconButton onClick={() => setCardOpen(true)} aria-label="Карточка клиента">
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <ClientCardDialog
                open={cardOpen}
                onClose={() => setCardOpen(false)}
                mode={orgId}
                phone={clientPhone}
                conversationId={conversationId}
                clientName={displayName}
            />

            {isGroup && (
                <GroupInfoDialog
                    open={groupInfoOpen}
                    onClose={() => setGroupInfoOpen(false)}
                    groupId={conversationId}
                    currentUserId={currentUserId}
                    fallbackTitle={displayName}
                    onGroupUpdated={handleGroupUpdated}
                    onGroupRemoved={() => {
                        setGroupInfoOpen(false);
                        onGroupRemoved?.();
                    }}
                />
            )}
        </Box>
    );
};
